/**
 * Support-Routing settings modal.
 *
 * Admin/manager picks which staff user gets auto-assigned when an
 * installer raises a support request of each category. Currently:
 *   • Delivery
 *   • Installation Support
 *   • Training Course   ← the reason this was built
 *
 * When set, `POST /api/support/tickets` in the backend automatically
 * fills `assigned_to` on any matching new request that doesn't already
 * have one. Nothing changes for tickets raised by staff on the board
 * itself — they still choose their own assignee.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, Save, Truck, Hammer, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

const CATEGORIES = [
  { key: 'delivery',             label: 'Delivery',             icon: Truck },
  { key: 'installation_support', label: 'Installation Support', icon: Hammer },
  { key: 'training_course',      label: 'Training Course',      icon: GraduationCap },
];

const SupportRoutingModal = ({ onClose }) => {
  const [teammates, setTeammates] = useState([]);
  const [routing, setRouting] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API_URL}/api/staff/support/staff-members`, { credentials: 'include' }),
          fetch(`${API_URL}/api/staff/support/routing`, { credentials: 'include' }),
        ]);
        if (r1.ok) setTeammates(await r1.json());
        if (r2.ok) setRouting((await r2.json()) || {});
      } catch (e) {
        toast.error('Could not load routing settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/support/routing`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(routing),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Save failed');
      toast.success('Routing saved — new requests will auto-assign');
      onClose?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="support-routing-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="font-bold text-lg">Auto-Route Requests</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Pick who owns each type of installer request. When someone raises one, we'll auto-assign it so it never sits in the "New" column waiting for triage.
          </p>
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.key}>
                  <Label className="flex items-center gap-1.5 mb-1">
                    <Icon className="w-4 h-4" /> {c.label}
                  </Label>
                  <select
                    value={routing[c.key] || ''}
                    onChange={(e) => setRouting((r) => ({ ...r, [c.key]: e.target.value || null }))}
                    data-testid={`routing-${c.key}`}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="">— No auto-route (stays unassigned) —</option>
                    {teammates.map((t) => (
                      <option key={t.user_id} value={t.user_id}>{t.name} ({t.role})</option>
                    ))}
                  </select>
                </div>
              );
            })
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={loading || saving} data-testid="routing-save" className="bg-slate-900 hover:bg-slate-800">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SupportRoutingModal;
